<template>
	<div>
		<!-- Account Overview -->
		<div class="card ov-card">
			<div class="card-header">
				<h4 class="mb-0">Overview</h4>
				<small>Profile summary of this user</small>
			</div>
			<div class="card-body">
				<div v-if="loading" class="ov-loading">
					<span class="spinner-border spinner-border-sm me-2" role="status"></span>Loading…
				</div>

				<template v-else>
					<!-- Quick stats -->
					<div class="ov-stats">
						<div class="ov-stat">
							<span class="ov-stat__label">Status</span>
							<span class="ov-badge ov-badge--success">Active</span>
						</div>
						<div class="ov-stat">
							<span class="ov-stat__label">Role</span>
							<span class="ov-stat__value">{{ user.role ? user.role.name : 'Author' }}</span>
						</div>
						<div class="ov-stat">
							<span class="ov-stat__label">Country</span>
							<span class="ov-stat__value">{{ user.country || '—' }}</span>
						</div>
					</div>

					<!-- Details -->
					<div class="ov-grid">
						<div class="ov-item">
							<span class="ov-item__label"><font-awesome-icon :icon="['fas', 'user']" class="me-2" />Full name</span>
							<span class="ov-item__value">{{ user.name || '—' }}</span>
						</div>
						<div class="ov-item">
							<span class="ov-item__label"><font-awesome-icon :icon="['fas', 'envelope']" class="me-2" />Email</span>
							<a v-if="user.email" :href="'mailto:' + user.email" class="ov-item__value ov-item__value--link">{{ user.email }}</a>
							<span v-else class="ov-item__value">—</span>
						</div>
						<div class="ov-item">
							<span class="ov-item__label"><font-awesome-icon :icon="['fas', 'phone']" class="me-2" />Phone</span>
							<span class="ov-item__value">{{ user.phone || '—' }}</span>
						</div>
						<div class="ov-item">
							<span class="ov-item__label"><font-awesome-icon :icon="['fas', 'calendar-alt']" class="me-2" />Member since</span>
							<span class="ov-item__value">{{ formatDate(user.created_at) }}</span>
						</div>
						<div class="ov-item ov-item--full">
							<span class="ov-item__label"><font-awesome-icon :icon="['fas', 'info-circle']" class="me-2" />About</span>
							<span class="ov-item__value" :class="{ 'ov-item__value--muted': !user.about }">
								{{ user.about || 'No description added yet.' }}
							</span>
						</div>
					</div>
				</template>
			</div>
		</div>
		<!-- #Account Overview -->
	</div>
</template>

<script>
import restApi from '../../../api'
import { mapState } from 'vuex'
import { handleErrors } from '../../../actions/helpers'
export default {
	name: 'Overview',
	data() {
		return {
			user: {},
			loading: false,
		}
	},
	computed: {
		...mapState(['authUser']),
	},
	created() {
		this.loadUser()
	},
	methods: {
		loadUser() {
			this.loading = true
			restApi
				.get('user/' + this.$route.params.id)
				.then(({ data }) => {
					this.loading = false
					this.user = data
				})
				.catch(error => {
					this.$swal.fire({
						icon: 'error',
						title: handleErrors(error, ''),
						timer: 6000,
					})
					this.loading = false
				})
		},
		formatDate(date) {
			if (!date) return '—'
			const d = new Date(date)
			if (isNaN(d.getTime())) return '—'
			return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
		},
	},
}
</script>

<style scoped>
.ov-card {
	border: 1px solid #e6e8ec;
	border-radius: 12px;
	box-shadow: none;
}

.ov-loading {
	display: flex;
	align-items: center;
	color: #6b7280;
	font-size: 0.9rem;
	padding: 0.5rem 0;
}

/* Quick stat tiles */
.ov-stats {
	display: grid;
	grid-template-columns: repeat(3, minmax(0, 1fr));
	gap: 12px;
	margin-bottom: 22px;
}

.ov-stat {
	display: flex;
	flex-direction: column;
	gap: 6px;
	padding: 12px 14px;
	border: 1px solid #eceef2;
	border-radius: 10px;
	background: #f9fafb;
}

.ov-stat__label {
	font-size: 0.72rem;
	font-weight: 700;
	text-transform: uppercase;
	letter-spacing: 0.04em;
	color: #8a909c;
}

.ov-stat__value {
	font-size: 15px;
	font-weight: 600;
	color: #111827;
}

.ov-badge {
	align-self: flex-start;
	display: inline-block;
	padding: 2px 10px;
	font-size: 12px;
	font-weight: 700;
	border-radius: 999px;
}

.ov-badge--success {
	background: #d1e7dd;
	color: #0f5132;
}

/* Details grid */
.ov-grid {
	display: grid;
	grid-template-columns: repeat(2, minmax(0, 1fr));
	gap: 16px 32px;
	border-top: 1px solid #f1f3f5;
	padding-top: 20px;
}

.ov-item {
	display: flex;
	flex-direction: column;
	gap: 4px;
	min-width: 0;
}

.ov-item--full {
	grid-column: 1 / -1;
}

.ov-item__label {
	font-size: 0.72rem;
	font-weight: 700;
	text-transform: uppercase;
	letter-spacing: 0.04em;
	color: #8a909c;
}

.ov-item__value {
	font-size: 14px;
	color: #1f2937;
	word-break: break-word;
}

.ov-item__value--link {
	color: #2b6cff;
	text-decoration: none;
}

.ov-item__value--link:hover {
	text-decoration: underline;
}

.ov-item__value--muted {
	color: #9ca3af;
	font-style: italic;
}

@media (max-width: 700px) {
	.ov-stats,
	.ov-grid {
		grid-template-columns: 1fr;
	}
}
</style>
